import { Suspense } from 'react';
import { isNotFound } from '@tanstack/react-router';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { Image } from '../../-components/image';
import { useCampaignDisplayContext } from './campaign-display-context';
// Imported statically on purpose. As a `lazy()` chunk it made the gallery blink on load:
// hydration reached the Suspense boundary before the chunk resolved, so React discarded the
// server-rendered carousel, painted the empty fallback and then re-created every <img>.
import GalleryContent, { GallerySkeleton } from './campaign-image-gallery-content';

export function CampaignImageGallery(props: { campaignId: string }) {
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign } = displayData;
		const heroImage = buildHeroLeadingImage(campaign.media?.location, campaign.heroImageAlt);
		const images = [
			...heroImage,
			...(campaign.carouselImages ?? []).map((img, index) => buildGalleryImage(img, index)),
		];
		if (images.length === 0) return null;
		return <GalleryContent images={images} />;
	}
	return (
		<Suspense fallback={<GallerySkeleton />}>
			<SuspensedCampaignImageGallery campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignImageGallery(props: { campaignId: string }) {
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	const heroImage = buildHeroLeadingImage(campaign.data.media?.location, campaign.data.heroImageAlt);
	const images = [
		...heroImage,
		...(campaign.data.carouselImages ?? []).map((img, index) => buildGalleryImage(img, index)),
	];
	if (images.length === 0) return null;

	return <GalleryContent images={images} />;
}

const SLIDE_HEIGHT = 286;
const SLIDE_ASPECT_RATIO = 413 / 286;
const SLIDE_CLASS = 'w-full! rounded-2xl object-cover';

// On mobile the hero image is merged into the carousel as its first slide (hidden from lg up,
// where the hero image is already shown next to the campaign title) so we gain vertical space.
function buildHeroLeadingImage(location: string | undefined, alt: string | undefined) {
	if (!location) return [];
	return [
		{
			key: `hero-${location}`,
			alt: alt ?? 'Campaign hero image',
			mobileOnly: true,
			image: (
				<Image
					src={location}
					height={SLIDE_HEIGHT}
					aspectRatio={SLIDE_ASPECT_RATIO}
					className={SLIDE_CLASS}
					alt={alt ?? 'Campaign hero image'}
				/>
			),
		},
	];
}

function buildGalleryImage(img: { location: string; alt?: string }, index: number) {
	return {
		key: img.location,
		alt: img.alt ?? `Campaign gallery ${index + 1}`,
		image: (
			<Image
				src={img.location}
				height={SLIDE_HEIGHT}
				aspectRatio={SLIDE_ASPECT_RATIO}
				className={SLIDE_CLASS}
				alt={img.alt ?? `Campaign gallery ${index + 1}`}
			/>
		),
	};
}
